import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, Analytics } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTrainingManagement } from '../hooks/useTrainingManagement';
import CourseGrid from '../components/CourseGrid';

export default function TrainingManagementPage() {
  const navigate = useNavigate();
  const { courses, loading, error, fetchCourses } = useTrainingManagement();
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourses = async () => {
    try {
      await fetchCourses();
    } catch (err) {
      console.error('Failed to load courses:', err);
    }
  };

  const handleCreateCourse = () => {
    navigate('/admin/training/courses/new');
  };

  const handleViewAnalytics = () => {
    navigate('/admin/training/analytics');
  };

  // Filter courses
  const filteredCourses = courses.filter(course => {
    if (statusFilter !== 'all' && course.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && course.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !course.title.toLowerCase().includes(query) &&
        !course.description.toLowerCase().includes(query)
      ) {
        return false;
      }
    }
    return true;
  });

  // Get unique categories
  const categories = Array.from(new Set(courses.map(c => c.category).filter(Boolean)));

  // Calculate stats
  const stats = {
    total: courses.length,
    published: courses.filter(c => c.status === 'PUBLISHED').length,
    draft: courses.filter(c => c.status === 'DRAFT').length,
    archived: courses.filter(c => c.status === 'ARCHIVED').length,
    totalEnrollments: courses.reduce((sum, c) => sum + (c.enrollmentCount || 0), 0),
    totalCertificates: courses.reduce((sum, c) => sum + (c.certificateCount || 0), 0),
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Training Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage training courses, quizzes, and certificates
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Analytics />}
            onClick={handleViewAnalytics}
          >
            View Analytics
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateCourse}
          >
            Create Course
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))"
        gap={2}
        mb={4}
      >
        <Box bgcolor="primary.main" color="white" p={2} borderRadius={1}>
          <Typography variant="h4" fontWeight="bold">
            {stats.total}
          </Typography>
          <Typography variant="body2">Total Courses</Typography>
        </Box>
        <Box bgcolor="success.main" color="white" p={2} borderRadius={1}>
          <Typography variant="h4" fontWeight="bold">
            {stats.published}
          </Typography>
          <Typography variant="body2">Published</Typography>
        </Box>
        <Box bgcolor="warning.main" color="white" p={2} borderRadius={1}>
          <Typography variant="h4" fontWeight="bold">
            {stats.draft}
          </Typography>
          <Typography variant="body2">Drafts</Typography>
        </Box>
        <Box bgcolor="info.main" color="white" p={2} borderRadius={1}>
          <Typography variant="h4" fontWeight="bold">
            {stats.totalEnrollments}
          </Typography>
          <Typography variant="body2">Total Enrollments</Typography>
        </Box>
        <Box bgcolor="secondary.main" color="white" p={2} borderRadius={1}>
          <Typography variant="h4" fontWeight="bold">
            {stats.totalCertificates}
          </Typography>
          <Typography variant="body2">Certificates Issued</Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="Search courses"
          placeholder="Search by title or description"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="PUBLISHED">Published</MenuItem>
          <MenuItem value="ARCHIVED">Archived</MenuItem>
        </TextField>
        <TextField
          select
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Course Grid */}
      {loading && courses.length === 0 ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <CourseGrid courses={filteredCourses} onRefresh={loadCourses} />
      )}
    </Container>
  );
}

